import { useEffect, useState } from "react"
import { router } from "@inertiajs/react"
import { Sparkles, Plus, Trash2, AlertCircle } from "lucide-react"
import { StepShell } from "../components/StepShell"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

function todayPlus(days) {
  const d = new Date()
  d.setDate(d.getDate() + days)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function blankCandidate() {
  return {
    goal: "",
    end_date: todayPlus(60),
    category: "professional",
    urgency: "medium",
    rationale: "",
    accepted: true,
    source: "manual",
  }
}

export function Imperatives({ report, data: _data }) {
  const initial = (report?.sections?.imperative_candidates || []).map((c) => ({
    goal: c.goal || "",
    end_date: c.end_date || c.suggested_end_date || todayPlus(60),
    category: c.category || "professional",
    urgency: c.urgency || "medium",
    rationale: c.rationale || "",
    accepted: c.accepted !== false,
    source: c.source || "manual",
  }))

  const [candidates, setCandidates] = useState(initial)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [hasFetched, setHasFetched] = useState(initial.length > 0)

  // Auto-fetch on first visit when there's no buffered content yet
  useEffect(() => {
    if (!hasFetched && candidates.length === 0) {
      generate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const generate = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch("/app/plan/intake/imperative_suggestions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json",
        },
        credentials: "same-origin",
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || "Couldn't generate suggestions.")
      }
      const body = await res.json()
      const aiCandidates = (body.candidates || []).map((c) => ({
        goal: c.goal || "",
        end_date: c.suggested_end_date || c.end_date || todayPlus(60),
        category: c.category || "professional",
        urgency: c.urgency || "medium",
        rationale: c.rationale || "",
        accepted: true,
        source: "ai",
      }))
      setCandidates((prev) => [...prev, ...aiCandidates])
      setHasFetched(true)
    } catch (e) {
      setError(e.message)
      setHasFetched(true)
    } finally {
      setLoading(false)
    }
  }

  const updateAt = (idx, patch) => {
    setCandidates((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)))
  }

  const removeAt = (idx) => {
    setCandidates((prev) => prev.filter((_, i) => i !== idx))
  }

  const addManual = () => {
    setCandidates((prev) => [...prev, blankCandidate()])
  }

  const submit = () => {
    setProcessing(true)
    router.patch(
      "/app/plan/intake",
      {
        step: "imperatives",
        imperative_candidates: candidates,
        advance: "1",
      },
      {
        preserveScroll: true,
        onFinish: () => setProcessing(false),
      },
    )
  }

  const skip = () => {
    setProcessing(true)
    router.patch(
      "/app/plan/intake",
      {
        step: "imperatives",
        imperative_candidates: [],
        advance: "1",
      },
      {
        preserveScroll: true,
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <StepShell
      title="30–90 Day Imperatives"
      subtitle="Imperatives are short, named commitments tied to a deadline and a real consequence. We've drafted a starting set from your scores and goals — accept, edit, or replace."
      onNext={submit}
      processing={processing}
      nextLabel="Continue"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={generate}
            disabled={loading}
          >
            <Sparkles className="w-4 h-4 mr-1.5" />
            {loading ? "Generating…" : (hasFetched ? "Regenerate AI suggestions" : "Generate AI suggestions")}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={addManual}>
            <Plus className="w-4 h-4 mr-1.5" /> Add manually
          </Button>
          <button
            type="button"
            onClick={skip}
            className="text-xs text-zinc-500 hover:text-zinc-900 underline ml-auto"
            disabled={processing}
          >
            Skip for now
          </button>
        </div>

        {error && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">{error}</p>
              <p className="text-xs mt-1">You can keep going by adding imperatives manually below.</p>
            </div>
          </div>
        )}

        {candidates.length === 0 && !loading && (
          <p className="text-sm text-zinc-500 text-center py-6">
            No imperatives yet. Generate AI suggestions or add one manually to continue.
          </p>
        )}

        {candidates.map((c, idx) => (
          <div
            key={idx}
            className={`rounded-lg border p-4 space-y-3 ${
              c.accepted ? "border-zinc-300 bg-white" : "border-zinc-200 bg-zinc-50 opacity-70"
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs px-2 py-0.5 rounded ${
                    c.source === "ai" ? "bg-kf-red/10 text-kf-red" : "bg-zinc-200 text-zinc-700"
                  }`}
                >
                  {c.source === "ai" ? "AI suggested" : "Manual"}
                </span>
                <label className="flex items-center gap-1 text-xs text-zinc-600">
                  <input
                    type="checkbox"
                    checked={c.accepted}
                    onChange={(e) => updateAt(idx, { accepted: e.target.checked })}
                  />
                  Keep this
                </label>
              </div>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="text-zinc-400 hover:text-red-600"
                aria-label="Remove"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div>
              <Label>Goal</Label>
              <Textarea
                rows={2}
                value={c.goal}
                onChange={(e) => updateAt(idx, { goal: e.target.value })}
                placeholder="What are you committing to?"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>End date</Label>
                <Input
                  type="date"
                  value={c.end_date}
                  min={todayPlus(1)}
                  onChange={(e) => updateAt(idx, { end_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={c.category} onValueChange={(v) => updateAt(idx, { category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                    <SelectItem value="spiritual">Spiritual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Urgency</Label>
                <Select value={c.urgency} onValueChange={(v) => updateAt(idx, { urgency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {c.rationale && (
              <p className="text-xs text-zinc-500 italic">Why: {c.rationale}</p>
            )}
          </div>
        ))}
      </div>
    </StepShell>
  )
}
